import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';

import { useTranslation } from 'react-i18next';

import { SimulationRunSettings, ResultsTimeWindowSettings } from '../../../../types/simulation';
import { fetchSimulationSettings } from '../../../../services/simulation/resultsApi';
import { liftangoBrandTokens } from '../../../../theme';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../../utils/iconFallbacks';
interface SimulationSettingsModalProps {
  open: boolean;
  resultsId: string | null | undefined;
  onClose: () => void;
}

const FONT_FAMILY = '"Roboto", sans-serif';

const COLORS = {
  bg: liftangoBrandTokens.simulationResults.sidebarBackground,
  text: liftangoBrandTokens.simulationResults.sidebarText,
  muted: liftangoBrandTokens.simulationResults.sidebarMutedText,
  border: liftangoBrandTokens.simulationResults.sidebarBorder,
  accent: liftangoBrandTokens.palette.primary,
  success: liftangoBrandTokens.palette.success,
} as const;

/** A single label + value row inside a settings section. */
const SettingRow: React.FC<{ label: string; value: React.ReactNode; icon?: React.ReactNode }> = ({
  label,
  value,
  icon,
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 1,
      borderBottom: `1px solid ${COLORS.border}`,
      gap: 2,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
      {icon && (
        <Box sx={{ display: 'flex', alignItems: 'center', color: COLORS.muted }}>{icon}</Box>
      )}
      <Typography
        variant="body2"
        sx={{ color: COLORS.muted, fontFamily: FONT_FAMILY, fontSize: '0.8125rem' }}
      >
        {label}
      </Typography>
    </Box>
    <Typography
      variant="body2"
      sx={{
        color: COLORS.text,
        fontFamily: FONT_FAMILY,
        fontSize: '0.8125rem',
        fontWeight: 600,
        textAlign: 'right',
        wordBreak: 'break-all',
      }}
    >
      {value}
    </Typography>
  </Box>
);

/** Maps a seat-type string to a representative MUI icon. */
const seatTypeIcon = (seatType: string): React.ReactNode => {
  const t = seatType.toLowerCase();
  if (t.includes('wheelchair') || t.includes('accessible')) {
    return <Icon size={14} name="wheelchair-move" />;
  }
  if (t.includes('child') || t.includes('infant') || t.includes('stroller')) {
    return <Icon size={14} d={ICON_FALLBACKS.ChildFriendly} />;
  }
  return <Icon size={14} d={ICON_FALLBACKS.EventSeat} />;
};

/** Boolean field rendered as a coloured chip. */
const BoolChip: React.FC<{ value: boolean }> = ({ value }) => {
  const { t } = useTranslation('simulation');
  return (
  <Chip
    label={value ? t('results.settingsModal.boolYes') : t('results.settingsModal.boolNo')}
    size="small"
    sx={{
      bgcolor: value ? `${COLORS.success}22` : 'rgba(0,0,0,0.04)',
      color: value ? COLORS.success : COLORS.muted,
      fontWeight: 600,
      fontSize: '0.75rem',
      height: 22,
    }}
  />
  );
};

/** Renders the time window settings accordion content in a two-column layout. */
const TimeWindowSection: React.FC<{
  tw: ResultsTimeWindowSettings;
  travelTimeMultiplier?: number | null;
}> = ({ tw, travelTimeMultiplier }) => {
  const { t } = useTranslation('simulation');
  const cfg = tw.settings;
  if (!cfg) return null;

  return (
    <Box sx={{ display: 'flex', gap: 3, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
      {/* ── Left column ── */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {cfg.slope != null && (
          <SettingRow
            label={t('results.settingsModal.rows.slope')}
            value={fmtNum(cfg.slope)}
            icon={<Icon size={14} d={ICON_FALLBACKS.TrendingUp} />}
          />
        )}
        {cfg.intercept != null && (
          <SettingRow
            label={t('results.settingsModal.rows.intercept')}
            value={t('results.settingsModal.unitSeconds', { value: fmtNum(cfg.intercept) })}
            icon={<Icon size={14} d={ICON_FALLBACKS.Timeline} />}
          />
        )}
        {cfg.window != null && (
          <SettingRow
            label={t('results.settingsModal.rows.window')}
            value={t('results.settingsModal.unitSeconds', { value: fmtNum(cfg.window) })}
            icon={<Icon size={14} name="clock" />}
          />
        )}
        {cfg.max_time_minutes != null && (
          <SettingRow
            label={t('results.settingsModal.rows.maxTime')}
            value={t('results.settingsModal.unitMinutes', { value: fmtNum(cfg.max_time_minutes) })}
            icon={<Icon size={14} name="hourglass-half" />}
          />
        )}
      </Box>

      {/* ── Right column ── */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {cfg.once_off_processing_time_seconds != null && (
          <SettingRow
            label={t('results.settingsModal.rows.onceOffProcessing')}
            value={t('results.settingsModal.unitSeconds', { value: fmtNum(cfg.once_off_processing_time_seconds) })}
            icon={<Icon size={14} d={ICON_FALLBACKS.Timer} />}
          />
        )}
        {cfg.processing_time && cfg.processing_time.length > 0 && (
          <Box sx={{ py: 1, borderBottom: `1px solid ${COLORS.border}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
              <Icon size={14} d={ICON_FALLBACKS.EventSeat} style={{ color: COLORS.muted }} />
              <Typography
                variant="body2"
                sx={{ color: COLORS.muted, fontFamily: FONT_FAMILY, fontSize: '0.8125rem' }}
              >
                {t('results.settingsModal.rows.processingTimesBySeat')}
              </Typography>
            </Box>
            {cfg.processing_time.map((pt) => (
              <Box
                key={pt.seat_type}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 0.5,
                  pl: 1.5,
                }}
              >
                <Chip
                  icon={seatTypeIcon(pt.seat_type) as React.ReactElement}
                  label={pt.seat_type}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(0,0,0,0.04)',
                    color: COLORS.muted,
                    fontSize: '0.75rem',
                    height: 20,
                    textTransform: 'capitalize',
                    '& .MuiChip-icon': { color: COLORS.muted, ml: '6px' },
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: COLORS.text, fontFamily: FONT_FAMILY, fontSize: '0.8125rem', fontWeight: 600 }}
                >
                  {t('results.settingsModal.unitSeconds', { value: fmtNum(pt.processing_time_seconds) })}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        {travelTimeMultiplier != null && (
          <SettingRow
            label={t('results.settingsModal.rows.travelTimeMultiplier')}
            value={fmtNum(travelTimeMultiplier)}
            icon={<Icon size={14} d={ICON_FALLBACKS.Speed} />}
          />
        )}
      </Box>
    </Box>
  );
};

/** Styled accordion matching the dark sidebar theme. */
const SettingsAccordion: React.FC<{
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}> = ({ title, children, defaultExpanded }) => (
  <Accordion
    defaultExpanded={defaultExpanded}
    disableGutters
    elevation={0}
    sx={{
      bgcolor: 'rgba(0,0,0,0.02)',
      border: `1px solid ${COLORS.border}`,
      borderRadius: '8px !important',
      mb: 1.5,
      '&:before': { display: 'none' },
      '&.Mui-expanded': { mb: 1.5 },
    }}
  >
    <AccordionSummary
      expandIcon={<Icon size={20} name="chevron-down" style={{ color: COLORS.muted }} />}
      sx={{
        px: 2,
        minHeight: 48,
        '& .MuiAccordionSummary-content': { my: 0 },
      }}
    >
      <Typography
        sx={{
          fontFamily: FONT_FAMILY,
          fontSize: '0.6875rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: COLORS.muted,
        }}
      >
        {title}
      </Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ px: 2, pt: 0, pb: 1.5 }}>
      {children}
    </AccordionDetails>
  </Accordion>
);

/** Formats a number value with up to 4 significant digits. */
const fmtNum = (v: number): string =>
  new Intl.NumberFormat('en-US', { maximumSignificantDigits: 4 }).format(v);

/** A label row displaying a full UUID with a one-click copy button. */
const CopyableIdRow: React.FC<{ label: string; id: string; omitBottomBorder?: boolean }> = ({
  label,
  id,
  omitBottomBorder = false,
}) => {
  const { t } = useTranslation('simulation');
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 0.75,
        borderBottom: omitBottomBorder ? 'none' : `1px solid ${COLORS.border}`,
        gap: 1,
      }}
    >
      <Typography
        variant="body2"
        sx={{ color: COLORS.muted, fontFamily: FONT_FAMILY, fontSize: '0.8125rem', flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            color: COLORS.text,
            fontFamily: '"Roboto Mono", "Courier New", monospace',
            fontSize: '0.75rem',
            wordBreak: 'break-all',
            textAlign: 'right',
          }}
        >
          {id}
        </Typography>
        <Tooltip title={copied ? t('results.settingsModal.copyTooltipCopied') : t('results.settingsModal.copyTooltipCopy')} placement="top" arrow>
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={{
              flexShrink: 0,
              color: copied ? COLORS.success : COLORS.muted,
              p: 0.25,
              '&:hover': { color: COLORS.text, bgcolor: 'rgba(0,0,0,0.04)' },
            }}
          >
            {copied ? <Icon size={14} name="check" /> : <Icon size={20} name="clipboard" />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

/** Renders an arbitrary JSONB simulation_settings object as nested key-value rows. */
const JsonKeyValueList: React.FC<{ data: Record<string, unknown>; depth?: number }> = ({
  data,
  depth = 0,
}) => (
  <>
    {Object.entries(data).map(([key, val]) => {
      if (val === null || val === undefined) return null;

      const label = key.replace(/_/g, ' ');

      if (typeof val === 'object' && !Array.isArray(val)) {
        return (
          <Box key={key} sx={{ mb: 0.5 }}>
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                color: COLORS.muted,
                fontFamily: FONT_FAMILY,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                mt: depth > 0 ? 1 : 0.5,
                mb: 0.25,
              }}
            >
              {label}
            </Typography>
            <Box sx={{ pl: 1.5, borderLeft: `2px solid ${COLORS.border}` }}>
              <JsonKeyValueList data={val as Record<string, unknown>} depth={depth + 1} />
            </Box>
          </Box>
        );
      }

      if (Array.isArray(val)) {
        return (
          <SettingRow
            key={key}
            label={label}
            value={`[${(val as unknown[]).slice(0, 3).join(', ')}${val.length > 3 ? `… +${val.length - 3}` : ''}]`}
          />
        );
      }

      if (typeof val === 'boolean') {
        return <SettingRow key={key} label={label} value={<BoolChip value={val} />} />;
      }

      return <SettingRow key={key} label={label} value={String(val)} />;
    })}
  </>
);

const SimulationSettingsModal: React.FC<SimulationSettingsModalProps> = ({
  open,
  resultsId,
  onClose,
}) => {
  const { t } = useTranslation('simulation');
  const [settings, setSettings] = useState<SimulationRunSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !resultsId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setSettings(null);

    fetchSimulationSettings(resultsId)
      .then((data) => {
        if (!cancelled) setSettings(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : t('results.settingsModal.failedToLoad'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, resultsId]);

  const pc = settings?.problem_config;
  const sc = settings?.scenario;
  const ss = settings?.simulation_settings;
  const tw = settings?.time_window_settings;
  const resolvedResultsId =
    resultsId != null && String(resultsId).trim() !== '' ? String(resultsId).trim() : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          bgcolor: COLORS.bg,
          color: COLORS.text,
          borderRadius: 2,
          backgroundImage: 'none',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: FONT_FAMILY,
          fontWeight: 700,
          fontSize: '1rem',
          color: COLORS.text,
          pb: 1,
          borderBottom: `1px solid ${COLORS.border}`,
        }}
      >
        {t('results.settingsModal.title')}
        {sc?.scenario_name && (
          <Typography
            component="span"
            sx={{
              display: 'block',
              fontSize: '0.8125rem',
              fontWeight: 400,
              color: COLORS.muted,
              fontFamily: FONT_FAMILY,
              mt: 0.25,
            }}
          >
            {sc.scenario_name}
          </Typography>
        )}
        {settings?.run_time && (
          <Typography
            component="span"
            sx={{
              display: 'block',
              fontSize: '0.75rem',
              fontWeight: 400,
              color: COLORS.muted,
              fontFamily: FONT_FAMILY,
            }}
          >
            {t('results.settingsModal.runTime', { time: new Date(settings.run_time).toLocaleString() })}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: COLORS.accent }} />
          </Box>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{ bgcolor: 'rgba(211,47,47,0.15)', color: '#f44336', border: '1px solid rgba(244,67,54,0.3)' }}
          >
            {error}
          </Alert>
        )}

        {settings && (
          <Box sx={{ mt: 1 }}>
            {/* ── Time Window Settings ── */}
            {tw && (
              <SettingsAccordion title={t('results.settingsModal.section.timeWindow')} defaultExpanded>
                <TimeWindowSection tw={tw} travelTimeMultiplier={pc?.travel_time_multiplier} />
              </SettingsAccordion>
            )}

            {/* ── Scenario ── */}
            <SettingsAccordion title={t('results.settingsModal.section.scenario')}>
              <>
                {sc ? (
                  <>
                    <SettingRow label={t('results.settingsModal.rows.scenarioName')} value={sc.scenario_name} />
                    {tw?.ride_settings?.rides_name && (
                      <SettingRow label={t('results.settingsModal.rows.ridesName')} value={tw.ride_settings.rides_name} />
                    )}
                    {pc?.iteration_count != null && (
                      <SettingRow label={t('results.settingsModal.rows.iterationCount')} value={pc.iteration_count} />
                    )}
                    <CopyableIdRow label={t('results.settingsModal.rows.scenarioId')} id={sc.scenario_id} />
                    <CopyableIdRow label={t('results.settingsModal.rows.fleetId')} id={sc.fleet_id} />
                    <CopyableIdRow label={t('results.settingsModal.rows.rideBatchId')} id={sc.ride_batch_id} />
                    <CopyableIdRow label={t('results.settingsModal.rows.timeWindowBatchId')} id={sc.time_window_batch_id} />
                  </>
                ) : null}
                {resolvedResultsId ? (
                  <CopyableIdRow label={t('results.settingsModal.rows.resultsId')} id={resolvedResultsId} omitBottomBorder />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 0.75,
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: COLORS.muted, fontFamily: FONT_FAMILY, fontSize: '0.8125rem' }}
                    >
                      {t('results.settingsModal.rows.resultsId')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: COLORS.muted,
                        fontFamily: FONT_FAMILY,
                        fontSize: '0.8125rem',
                        fontStyle: 'italic',
                      }}
                    >
                      {t('results.settingsModal.rows.unavailable')}
                    </Typography>
                  </Box>
                )}
              </>
            </SettingsAccordion>

            {/* ── Simulation Settings (only when data is present) ── */}
            {ss && (
              <SettingsAccordion title={t('results.settingsModal.section.simulationRuntime')}>
                <JsonKeyValueList data={ss} />
              </SettingsAccordion>
            )}
          </Box>
        )}
      </DialogContent>

      <Divider sx={{ borderColor: COLORS.border }} />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button
          onClick={onClose}
          sx={{
            color: COLORS.muted,
            fontFamily: FONT_FAMILY,
            '&:hover': { color: COLORS.text, bgcolor: 'rgba(0,0,0,0.03)' },
          }}
        >
          {t('results.settingsModal.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SimulationSettingsModal;
